import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

// APIs
import { GetDepartments } from "../../../api/masters/departmentAPI";
import { BulkImportCase, StoreCase } from "../../../api/masters/caseAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// ✅ Validation Schema (aligned with your Mongoose model)
const schema = yup.object().shape({
  name: yup.string().required("Case name is required"),
  departmentId: yup.string().required("Department is required"),
  ob: yup.string().oneOf(["Yes", "No"]).required()
});

const AddCase = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);

  const { register, control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      departmentId: "",
      ob: "No"
    }
  });

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: "LOADER_START" });

        await fetchDepartments();
      } catch (error) {
        showToast("error", error);
      } finally {
        dispatch({ type: "LOADER_STOP" });
      }
    };

    loadData();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const res = await StoreCase(data);
      if (res.success) {
        showToast("success", res.message);
        navigate(`/masters/case/list`);
      } else {
        showToast("error", res.message);
      }
    } catch (err) {
      showToast("error", err.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "name", label: "Case Name", sample: "Case 1" },
    { key: "department", label: "Department", sample: "Department 1" },
    { key: "ob", label: "OB", sample: "Yes/No" }
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportCase(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/case/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Case" title="Add Case" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Case</h5>
                <Link to={`/masters/case/list`}>
                  <button className="btn btn-primary" id="list">
                    List Case
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Case
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Name *</label>
                      <input
                        className="form-control"
                        placeholder="Case Name"
                        {...register("name")}
                      />
                      {errors.name &&
                        <p className="text-danger">
                          {errors.name.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Department *</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dep =>
                          <option key={dep._id} value={dep._id}>
                            {dep.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>OB *</label>
                      <select className="form-control" {...register("ob")}>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="cases"
              sampleFileName="case_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddCase;
